import React from 'react';
import { useGlobalState } from '../../components/GlobalStateProvider';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { reportSwallowed } from '@/lib/sentry';

export default function IntakeReview() {
  const gs = useGlobalState() as any;
  const [loading, setLoading] = React.useState(true);
  const [packet, setPacket] = React.useState<any>(null);
  const [isFinalizing, setIsFinalizing] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null);
  const [requestId, setRequestId] = React.useState<string | null>(null);
  const inviteId = React.useMemo(() => new URLSearchParams(window.location.search).get('invite') || '', []);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!inviteId) return;
        const p = await gs.getIntakeByInvite?.(inviteId);
        if (!cancelled) setPacket(p);
      } catch (_e) {
        // Don't expose raw error messages
        console.warn('[IntakeReview] load failed');
        reportSwallowed('IntakeReview.load', _e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [inviteId, gs]);

  if (!inviteId) {
    return (
      <main className="container mx-auto p-6" role="main" aria-label="Intake review page">
        <Card aria-label="Error: Missing invite ID">
          <CardHeader>
            <CardTitle>Intake Review</CardTitle>
          </CardHeader>
          <CardContent>
            <p role="alert">Missing invite id.</p>
          </CardContent>
        </Card>
      </main>
    );
  }

  if (loading) {
    return (
      <main
        className="container mx-auto p-6 text-muted-foreground"
        role="main"
        aria-busy="true"
        aria-label="Loading intake review"
      >
        <div role="status" aria-live="polite">
          Loading intake data...
        </div>
      </main>
    );
  }

  if (!packet) {
    return (
      <main className="container mx-auto p-6" role="main" aria-label="Intake review page">
        <Card aria-label="No intake found">
          <CardHeader>
            <CardTitle>Intake Review</CardTitle>
          </CardHeader>
          <CardContent>
            <p role="alert">No intake packet found for this invite.</p>
          </CardContent>
        </Card>
      </main>
    );
  }

  const answers = (packet.answers || []) as Array<any>;
  const fn = answers.find((a: any) => a.section === 'personal' && a.key === 'full_name')?.value;
  const age = answers.find((a: any) => a.section === 'personal' && a.key === 'age')?.value;
  const goalsCount = (packet.goals || []).length;
  const trackersCount = (packet.trackers || []).length;
  const mergedAt = (packet as any).mergedAt;

  const handleFinalize = async () => {
    setIsFinalizing(true);
    setErrorMsg(null);
    setRequestId(null);
    try {
      const res = await gs.finalizeIntake?.(packet.id);
      toast.success('Intake finalized and client created');
      (window as any).__lastFinalizeClientId = res?.clientId;
      window.location.href = `/clients/${res?.clientId}/overview?source=intake`;
    } catch (e: any) {
      // Don't expose raw error messages - show generic message with request ID
      const rid = e?.requestId || crypto.randomUUID().slice(0, 8);
      setRequestId(rid);
      setErrorMsg('Failed to finalize intake. Please try again or contact support.');
      try {
        toast.error('Failed to finalize intake');
      } catch {
        // OBS-11 a-ignore: the inline error message is already set above; this toast is extra.
      }
    } finally {
      setIsFinalizing(false);
    }
  };

  return (
    <main className="container mx-auto p-6" role="main" aria-label="Intake review page">
      <Card aria-label="Intake review details">
        <CardHeader>
          <CardTitle>Intake Review</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Status region for screen readers */}
            <div className="sr-only" role="status" aria-live="polite">
              {loading ? 'Loading...' : `Intake loaded: ${goalsCount} goals, ${trackersCount} trackers`}
            </div>

            {/* Personal section */}
            <section aria-label="Personal information">
              <h3 className="text-sm text-muted-foreground">Personal</h3>
              <p className="text-foreground">
                {fn || '—'}
                {age ? `, ${age}` : ''} • {packet.clientEmail}
              </p>
            </section>

            {/* Goals section */}
            <section aria-label={`Goals, ${goalsCount} items`}>
              <h3 className="text-sm text-muted-foreground mb-1">Goals</h3>
              <ul className="list-disc list-inside text-foreground" role="list" aria-label="Goals list">
                {(packet.goals || []).map((g: any) => (
                  <li
                    key={g.id}
                    role="listitem"
                    aria-label={`Goal: ${g.title}${g.targetValue != null ? `, target ${g.targetValue}${g.unit ? ' ' + g.unit : ''}` : ''}`}
                  >
                    {g.title} {g.targetValue != null ? `→ ${g.targetValue}${g.unit ? ' ' + g.unit : ''}` : ''}
                  </li>
                ))}
                {goalsCount === 0 && <li className="text-muted-foreground">None</li>}
              </ul>
            </section>

            {/* Trackers section */}
            <section aria-label={`Trackers, ${trackersCount} items`}>
              <h3 className="text-sm text-muted-foreground mb-1">Trackers</h3>
              <ul className="list-disc list-inside text-foreground" role="list" aria-label="Trackers list">
                {(packet.trackers || []).map((t: any) => (
                  <li
                    key={t.id}
                    role="listitem"
                    aria-label={`Tracker: ${t.title}${t.unit ? `, unit ${t.unit}` : ''}${t.cadence ? `, ${t.cadence}` : ''}`}
                  >
                    {t.title}
                    {t.unit ? ` (${t.unit})` : ''} {t.cadence ? `• ${t.cadence}` : ''}
                  </li>
                ))}
                {trackersCount === 0 && <li className="text-muted-foreground">None</li>}
              </ul>
            </section>

            {/* Action section */}
            <section className="pt-2 space-y-2" aria-label="Actions">
              {errorMsg && (
                <div className="text-sm text-red-600" role="alert" aria-live="assertive">
                  {errorMsg}
                  {requestId && <span className="text-xs text-muted-foreground ml-2">(Ref: {requestId})</span>}
                </div>
              )}
              <Button
                disabled={!!mergedAt || isFinalizing}
                aria-disabled={!!mergedAt || isFinalizing}
                aria-busy={isFinalizing}
                aria-label={
                  mergedAt
                    ? `Already merged on ${new Date(mergedAt).toLocaleString()}`
                    : isFinalizing
                      ? 'Finalizing intake...'
                      : 'Finalize and create client'
                }
                onClick={handleFinalize}
                className="focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                {mergedAt
                  ? `Merged on ${new Date(mergedAt).toLocaleString()}`
                  : isFinalizing
                    ? 'Finalizing…'
                    : 'Finalize & create client'}
              </Button>
              {mergedAt && (
                <Button
                  variant="outline"
                  onClick={() => {
                    const id = (window as any).__lastFinalizeClientId;
                    window.location.href = id ? `/clients/${id}/overview?source=intake` : '/';
                  }}
                  aria-label="View the created client profile"
                  className="focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  View client
                </Button>
              )}
            </section>
          </div>
        </CardContent>
      </Card>
    </main>
  );
}
